import { message } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { SalonAPI } from "../../api/Salon";
import { SalonServiceTranslationPayload } from "../../@type/salonService.types";
import ManageTranslationModal, {
  ManageTranslationItem,
} from "../common/ManageTranslationModal";

interface ServiceTranslationModalProps {
  open: boolean;
  outletId: string;
  serviceId?: number;
  serviceName?: string;
  languages: Languages[];
  onCancel: () => void;
  onSuccess?: () => void;
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error) return error.message;
  return fallback;
};

const ServiceTranslationModal: React.FC<ServiceTranslationModalProps> = ({
  open,
  outletId,
  serviceId,
  serviceName,
  languages,
  onCancel,
  onSuccess,
}) => {
  const [translations, setTranslations] = useState<ManageTranslationItem[]>([]);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const requestRef = useRef(0);

  useEffect(() => {
    requestRef.current += 1;
    const requestId = requestRef.current;

    setTranslations([]);
    setLoadedKey(null);
    if (!open) {
      setLoading(false);
      return;
    }
    if (!outletId || !serviceId) return;

    const fetchTranslations = async () => {
      try {
        setLoading(true);
        const response = await SalonAPI.getServiceTranslations(outletId, serviceId);
        if (requestRef.current !== requestId) return;
        setTranslations(
          (response.data.data.translations || []).map((translation) => ({
            ...translation,
            translation_name: translation.translation_name || "",
          }))
        );
        setLoadedKey(`${outletId}:${serviceId}`);
      } catch (error) {
        if (requestRef.current !== requestId) return;
        console.error("Error fetching service translations:", error);
        message.error(getErrorMessage(error, "Failed to load service translations"));
      } finally {
        if (requestRef.current === requestId) setLoading(false);
      }
    };

    fetchTranslations();
  }, [open, outletId, serviceId]);

  const handleSave = useCallback(
    async (items: ManageTranslationItem[]) => {
      if (!outletId || !serviceId) return;

      // Skip languages that have no translation yet and were left empty.
      const payload: SalonServiceTranslationPayload[] = items
        .map((item) => ({
          translation_id: item.translation_id,
          translation_name: item.translation_name,
          language_id: item.language_id,
          language_code: item.language_code,
        }))
        .filter(
          (item) =>
            item.translation_id !== null || String(item.translation_name).trim().length > 0
        );

      try {
        await SalonAPI.saveServiceTranslations(outletId, serviceId, payload);
      } catch (error) {
        console.error("Error updating service translations:", error);
        message.error(getErrorMessage(error, "Failed to update service translations"));
        throw error;
      }
      message.success("Service translations updated successfully");
      onSuccess?.();
    },
    [outletId, serviceId, onSuccess]
  );

  const handleAutoTranslate = useCallback(
    async (text: string, sourceLanguageCode: string) => {
      const response = await SalonAPI.autoTranslateServiceName(outletId, {
        text,
        source_language_code: sourceLanguageCode,
      });
      return response.data.data.translations || {};
    },
    [outletId]
  );

  // Stale ready would permanently hydrate the previous service's values (inner modal never overwrites).
  const ready = !loading && loadedKey === `${outletId}:${serviceId}`;

  return (
    <ManageTranslationModal
      open={open}
      subtitle={serviceName}
      languages={languages}
      translations={translations}
      loading={loading}
      ready={ready}
      fallbackSourceText={serviceName}
      onCancel={onCancel}
      onSave={handleSave}
      onAutoTranslate={handleAutoTranslate}
    />
  );
};

export default ServiceTranslationModal;
